import type { ReplyActionsResp } from '@lib/care-api';
import { REPLY_OP_WORDING, answerChangedWords } from '@lib/reply-groups';
import { isStagedRow, nothingQueuedSentence, queuedCountSentence, stagedRowSentence } from '@lib/staged-presses';

/**
 * ROW 391's helpers that outlive its buttons: the sentence for a reply press's answer, and the entries a
 * press took. I3 (William, 16 Oct 2026 about 10 PM: remove) took the reply group buttons (Opt In, Opt Out,
 * Help, All other) and their presses off the Inbound queue's flat view; the five buttons on top
 * (QueueActionBar) are the presses there now. The drawer and the Opt In queue still print these sentences.
 */

/**
 * One sentence for the answer to a press, in her words: what landed, and each row that did not, with why.
 *
 * WAVE 3 ITEM 4: a row the server QUEUED for approval (`staged`) is never counted as done and never
 * worded as done: the sentence starts "Queued for approval" and names no done word for it. One queued
 * entry prints its own sentence (the server's, else the contract's built from the button's words);
 * several print how many of how many. With no queued row the sentence is what it always was.
 */
export function pressSentence(label: string, answers: ReplyActionsResp[]): string {
  const rows = answers.flatMap((a) => a.results ?? []);
  const staged = rows.filter(isStagedRow);
  const done = rows.filter((r) => r.status === 'done' && !isStagedRow(r));
  const notDone = rows.filter((r) => r.status !== 'done' && !isStagedRow(r));
  const op = answers[0]?.op;
  const doneWords = op ? REPLY_OP_WORDING[op].done : 'done';
  let s = '';
  if (staged.length) {
    s = staged.length === 1 && rows.length === 1
      ? stagedRowSentence(staged[0], label)
      : queuedCountSentence(label, staged.length, rows.length);
    // A press that also acted at once on other entries says so after the queued ones, in its own words.
    if (done.length) s += ` ${label}: ${done.length} of ${rows.length} ${doneWords}.`;
  } else if (rows.length && !done.length && answers.some((a) => a.staged === true)) {
    // The server took the press as one to queue and every entry was refused: nothing was queued, and
    // no done word is printed for a press that could not have been done.
    s = nothingQueuedSentence(label, rows.length);
  } else {
    s = `${label}: ${done.length} of ${rows.length} ${doneWords}.`;
  }
  if (notDone.length) {
    s += ` Still in the queue: ${notDone.map((r) => `${r.eventId} (${r.reason || r.code})`).join('; ')}.`;
  }
  const note = answers.find((a) => a.kitRequestNote)?.kitRequestNote ?? answers.find((a) => a.platformNote)?.platformNote
    ?? answers.find((a) => a.referralNote)?.referralNote;
  if (done.length && note) s += ` ${note}`;
  const off = rows.find((r) => r.platform && !r.platform.attempted);
  if (done.length && op === 'opt-out' && off?.platform) s += ` Texting platform: ${off.platform.reason}.`;
  // Approval 187 (d): approved, and the member changed their answer on the way. The office calls them.
  const changed = done.filter((r) => r.answerChanged);
  if (changed.length) {
    s += ` Call first: ${changed.map((r) => `${r.eventId} (${answerChangedWords(r.answerChanged!)})`).join('; ')}.`;
  }
  return s;
}

/**
 * The entries a press TOOK: done, or (wave 3 item 4) queued for approval. Never a refused or a failed
 * one. These are the rows a page unticks after the press; with no queued row it is the done rows alone.
 */
export function handledIds(answers: ReplyActionsResp[]): string[] {
  return answers.flatMap((a) => a.results ?? []).filter((x) => x.status === 'done' || isStagedRow(x)).map((x) => x.eventId);
}
